import { NextRouter } from 'next/router';
import { stringify } from 'querystring';
import { camelizeKeys } from 'humps';

import { SEARCH_TYPE, NavigationSearchProps, OhouseNavigation, SearchItem, Supplementary, SearchQuery, SearchConsulting, SearchCategory } from '@bucketplace/design-system';
import { bucketLog } from '@bucketplace/bucket-log';

import { UserInfoType } from 'types';
import { OHS_HOST } from 'common';
import {
  appendHistory,
  getHistories,
  getSearchPath,
  removeHistory,
  removeHistoryAll,
  setHistories,
} from 'utils/search';
import { getDeepLink, getQueryString } from 'utils/tracking';

import { CONTENTS_EXPERIMENT_TYPE, SuperLink } from 'components/Layout';
import { logAmplitude } from './tracking';
import { handleLog } from './log';
import { getUserMenu } from './menu';

interface GetAutoCompleteCategoryKeyword {
  query: string;
  results: (SearchQuery | SearchConsulting | SearchCategory)[];
}

const AUTOCOMPLETE_BASE_URL_STAGE = 'https://autocomplete.stage.datahou.se';
const AUTOCOMPLETE_BASE_URL_PROD = 'https://autocomplete.datahou.se';
const AUTOCOMPLETE_API_VERSION = 2;

export const getSupplementary = (
  userInfo: UserInfoType,
  pathname: string,
  signOut: () => void,
  experiments?: Record<string, string>
): Supplementary => ({
  userProfile: userInfo?.user,
  userMenu: getUserMenu(signOut, userInfo?.user),
  scrap: {
    href: `${OHS_HOST}/users/${userInfo?.user?.userId}/collections`,
  },
  cart: {
    count: userInfo?.cartCount ?? 0,
    href: `${OHS_HOST}/cart`,
  },
  notification: {
    count: userInfo?.notificationCount ?? 0,
    href: `${OHS_HOST}/notifications/feed`,
  },
  signin: {
    href: `${OHS_HOST}/users/sign_in?redirect_to=/events${encodeURIComponent(pathname)}`,
  },
  signup: {
    href: `${OHS_HOST}/normal_users/new`,
  },
  cs: {
    href: `${OHS_HOST}/customer_center`,
  },
  seller: {
    href: `${OHS_HOST}/sellers/auth/request_multi_factor_auth`,
  },
  uploadMenuLinks: {
    photo: `${OHS_HOST}/contents/card_collections/new${experiments.contents === CONTENTS_EXPERIMENT_TYPE.NEW ? '?media=photo' : ''}`,
    project: `${OHS_HOST}/projects/write`,
    advice: `${OHS_HOST}/advices/write`,
    productionReview: `${OHS_HOST}/production_reviews/write`,
    expertReview: `${OHS_HOST}/expert_reviews/new?source=6`,
  },
});

const handleSubmit = (
  value: string,
  router: NextRouter,
  searchType: typeof SEARCH_TYPE[keyof typeof SEARCH_TYPE]
): void => {
  if (value == null || value.trim() === '') {
    // Do nothing if value is empty
    return;
  }
  appendHistory(value);
  const path = getSearchPath(searchType, value);

  logAmplitude('검색어 Searched', {
    search_type: searchType,
    query_type: '직접 입력',
    query: value,
  });

  router.push(`${OHS_HOST}${path}`);
};

const formatAutoCompleteKeywords = (data: unknown): SearchItem[] => {
  const { results } =
    camelizeKeys(data) as GetAutoCompleteCategoryKeyword;
  return results.map((item) => {
    return {
      type: item.dataType,
      value: item,
      query: item.orgDisplay,
    } as SearchItem;
  });
};

const getSearchProps = (router: NextRouter)
: NavigationSearchProps => {
  const searchType = 'store' as typeof SEARCH_TYPE[keyof typeof SEARCH_TYPE];
  return {
    searchType,
    onFetch: async (query: string) => {
      const isProd = process.env.APP_PROFILE === 'prod';
      const url = isProd ? AUTOCOMPLETE_BASE_URL_PROD : AUTOCOMPLETE_BASE_URL_STAGE;
      const res = await fetch(`${url}/commerce?${
        stringify({
          query,
          v: `v${AUTOCOMPLETE_API_VERSION}`,
        })}`, {
        method: 'GET',
        credentials: 'same-origin',
      });
      const json = await res.json();

      return formatAutoCompleteKeywords(json);
    },
    onSubmitItem: (item: SearchItem) => {
      handleSubmit(item.query, router, searchType);
    },
    onSubmit: (value: string) => {
      handleSubmit(value, router, searchType);
    },
    onInputFocus: () => {
      bucketLog({
        category: 'click',
        section: '검색',
      });
      logAmplitude('검색 필드 Clicked', {
        search_type: searchType,
      });
    },
    placeholder: '쇼핑 검색',
    historyStorage: {
      get: getHistories,
      set: setHistories,
      append: appendHistory,
      remove: removeHistory,
      removeAll: removeHistoryAll,
    },
  };
};

const handleDownloadApp = (): void => {
  window.location.href = getDeepLink(
    { sub_id_1: 'web_left_bnr' }
  );
};

export const getNavigationProps = ({
  navigationMenu,
  router,
  userInfo,
  signOut,
  experiments,
}): OhouseNavigation => {
  const { query, asPath } = router;
  const search = `?${getQueryString(query)}`;

  return {
    menuSchema: navigationMenu,
    searchOptions: getSearchProps(router),
    supplementary: getSupplementary(userInfo, asPath, signOut, experiments),
    linkComponent: SuperLink,
    fallbackMenuSchema: [],
    onLog: handleLog,
    onLogout: signOut,
    onAppDownload: handleDownloadApp,
    location: { pathname: asPath, search },
    logo: {
      href: OHS_HOST,
    },
  };
};
